<template>
  <aha-modal
    :title="title"
    @hide="$emit('hide')"
    action-position="justify-center"
  >
    <div v-if="arePresentationsLoading" class="flex items-center justify-center h-52">
      <aha-spinner size="lg" variant="tail" />
    </div>

    <template v-else>
      <div v-if="hasError" class="flex flex-col justify-center h-52">
        <div class="text-center">
          <div>Cannot retrieve your presentation list.</div>

          <div>
            <span>Please</span>
            <a @click.stop="getPresetations()" class="cursor-pointer primary-color">
              click here
            </a>
            <span>to try again.</span>
          </div>
        </div>
      </div>

      <template v-else>
        <div
          v-if="allowToCopyToPresentations.length === 0"
          class="flex flex-col justify-center h-52"
        >
          <div class="text-center">
            Has no presentation to copy to.
          </div>
        </div>

        <template v-else>
          <div class="pb-2 font-bold">
            Choose destination to copy to:
          </div>

          <div class="mb-3 overflow-auto max-h-52">
            <div
              class="folder-item"
              :key="folder.folderId"
              v-for="(folder) in folders"
            >
              <div class="mb-1 font-size-xs text-base-90" v-if="folders.length !== 1">
                {{ folder.folderName }}
              </div>

              <div
                :class="[
                  presentation.id === selectedPresentationId
                    ? 'presentation-item--active'
                    : 'presentation-item'
                ]"
                :key="presentation.id"
                @click="handlePresentationItemClick(presentation)"
                v-for="
                  (presentation) in allowToCopyToPresentations
                    .filter((item) => item.folderId === folder.folderId)
                "
              >
                <input
                  class="mr-2"
                  type="radio"
                  :checked="presentation.id === selectedPresentationId"
                >
                <span class="truncate">{{ presentation.name }}</span>
                <span class="ml-1 text-base-70">({{ presentation.accessCode }})</span>
              </div>
            </div>
          </div>

          <strong class="mb-2 mr-2 d-inline-block">Insert after slide:</strong>

          <aha-select
            class="py-3"
            v-model="selectedSlideOrder"
            :disabled="areSlidesLoading"
          >
            <aha-select-option value="-1" hidden>
              Loading slides...
            </aha-select-option>

            <aha-select-option value="0">
              (Make it first)
            </aha-select-option>

            <aha-select-option
              :key="slide.id"
              :value="slide.order"
              v-for="(slide, index) in slides"
            >
              <span>{{ index + 1 }}</span>
              <span> - </span>
              <span>
                {{ renderSlideTitle(slide) }}
              </span>
            </aha-select-option>
          </aha-select>

          <div
            v-if="selectedSlides.some(item => item.type === 'leaderboard')"
            class="mb-2 text-xs text-base-70"
          >
            Rankings on the Leaderboard are calculated live based on the previous Quiz slides.
            To copy the rankings data, you just need to copy all the previous Quiz slides over.
          </div>

          <div
            v-if="selectedSlides.some(item => item.type === 'question')"
            class="mb-2 text-xs text-base-70"
          >
            If checked, all Q&A data from this presentation will
            be merged into the destination presentation.
          </div>

          <aha-checkbox v-model="isIncludingResponseData">
            Includes response data
          </aha-checkbox>
        </template>
      </template>
    </template>

    <template
      #action
      v-if="!hasError && allowToCopyToPresentations.length !== 0 && !arePresentationsLoading"
    >
      <div class="flex justify-center min-h-8">
        <aha-button variant="primary" @click="handleCopyButtonClick()">
          <span>
            <span>Copy</span>
            <span
              v-if="isSlideCopying"
              class="ml-1 loading-spinner"
            />
          </span>
        </aha-button>
      </div>
    </template>
  </aha-modal>
</template>

<script>
import Utils from '@/util'
import Config from '@/config'
import Constant from '@/constant'

// api
import FolderApi from '@/api/folder'
import PresentationApi from '@/api/presentation'
import mixpanelUtils from '@/utils/mixpanel.utils'

export default {
  props: {
    getItems: {
      type: Function,
      default: () => [],
    },
    title: {
      type: String,
      default: null,
    },
  },
  data: () => {
    return {
      slides: [],
      folders: [],
      hasError: false,
      isSlideCopying: false,
      selectedSlideOrder: 0,
      areSlidesLoading: false,
      selectedPresentationId: null,
      isIncludingResponseData: true,
      arePresentationsLoading: false,
      allowToCopyToPresentations: [],
      selectedSlides: [],
    }
  },
  async mounted() {
    this.selectedSlides = this.getItems()
    const userSettings = this.$store.state.currentUser.rememberSettings
    if (userSettings && userSettings.isIncludingResponseData !== undefined) {
      this.isIncludingResponseData = userSettings.isIncludingResponseData
    } else {
      this.isIncludingResponseData = true
    }
    this.getPresetations()
  },
  methods: {
    async getPresetations() {
      this.hasError = false
      this.arePresentationsLoading = true
      const isFromWorkspace = this.$store.state.workingMode === 'team'
      try {
        const { presentations } = await PresentationApi.getPresentations({
          limit: 500,
          includeShared: true,
          fromWorkspace: isFromWorkspace,
        })

        // exclude current presentation
        this.allowToCopyToPresentations = presentations.filter((presentation) => {
          return presentation.id !== this.$store.state.presentation.id
        })

        if (this.allowToCopyToPresentations.length === 0) {
          this.arePresentationsLoading = false
          return
        }

        // get slides by presentation id
        this.selectedPresentationId = this.allowToCopyToPresentations[0].id
        const res = await this.$http.get(Config.api.getSlides, {
          params: {
            presentationId: this.selectedPresentationId,
          },
        })

        this.slides = res.body
        this.selectedSlideOrder = 0

        // get folder list
        this.folders = await FolderApi.getFolders()

        this.folders.unshift({
          folderId: null,
          folderName: 'My presentations',
        })

        this.folders = this.folders.filter((folder) => {
          return this.allowToCopyToPresentations.some((presentation) => {
            return presentation.folderId === folder.folderId
          })
        })
      } catch (error) {
        this.hasError = true
        Utils.gaTrackEvent('Presentation Editing', 'Copy slide', 'Error when loading presentations')

        throw error
      } finally {
        this.arePresentationsLoading = false
      }
    },
    handleCheckboxClick() {
      this.isIncludingResponseData = !this.isIncludingResponseData
    },
    renderSlideTitle(slide) {
      const { title, type, slideType } = slide

      if (title && title.trim()) {
        const trimedTitle = title.trim()

        if (trimedTitle.length > 40) {
          return `${trimedTitle.slice(0, 40)}...`
        }

        return trimedTitle
      }

      return this.convertTypeToName(type, slideType)
    },
    convertTypeToName(type, slideType) {
      if (type === 'pickAnswer') {
        if (slideType === 'imageChoice') return 'Pick Image'
        if (slideType === 'typeAnswer') return 'Type Answer'
      }

      let types = []
      Constant.allTypeSections.forEach((item) => {
        types = types.concat(item.slideTypes)
      })

      const matchType = types.find((item) => item.type === type)
      return matchType.name
    },
    async handleCopyButtonClick() {
      if (this.isSlideCopying) return

      const payload = {
        duplicatedSlideId: this.selectedSlides.map((item) => item.id),
        insertAfter: this.selectedSlideOrder,
        isDuplicatingToOtherPresentation: true,
        isIncludingResponseData: this.isIncludingResponseData,
        destinatedPresentationId: this.selectedPresentationId,
      }

      this.isSlideCopying = true
      const { accessCode, name } = this.allowToCopyToPresentations
        .find((item) => item.id === this.selectedPresentationId)

      try {
        await this.$http.post(Config.api.duplicateSlideToOtherPresentation, payload)
        Utils.gaTrackEvent('Presentation Editor', 'Create new slide', 'Side left > Slide menu > Copy to', null, {
          ...mixpanelUtils.getPresentationContext(this.$store.state.presentation),
          slideId: this.$store.state.slideActive.id,
        })

        const toastMessage = this.selectedSlides.length === 1
          ? 'Copy 1 slide successfully'
          : `Copy ${this.selectedSlides.length} slides successfully`
        Utils.showToastSuccess(`${toastMessage} to presentation "${name}" [${accessCode}]`)
      } catch (error) {
        Utils.handleError(error.body.message)
        Utils.showToastError('Failed to copy slide.')
        return
      } finally {
        this.isSlideCopying = false
      }
      await this._updateUser()
      this.closeSlideCopyPopover()
      this.$emit('hide')
    },

    async _updateUser() {
      try {
        const payload = {
          isIncludingResponseData: this.isIncludingResponseData,
        }
        const result = await this.$http
          .put(Config.api.updateUserSettings, payload)
        this.$store.state.currentUser.rememberSettings
        = result.body.rememberSettings
      } catch (error) {
        // wait
      }
    },
    closeSlideCopyPopover() {
      const newSlides = this.$store.state.presentation.slides.map((item) => {
        return { ...item, isSlideCopyPopoverShowing: false }
      })

      this.$store.state.presentation.slides = newSlides
    },
    async handlePresentationItemClick(presentation) {
      // won't handle event if click on the same presentation
      if (this.selectedPresentationId === presentation.id) {
        return
      }

      this.hasError = false
      this.selectedSlideOrder = -1
      this.areSlidesLoading = true

      this.selectedPresentationId = presentation.id

      try {
        const res = await this.$http.get(Config.api.getSlides, {
          params: {
            presentationId: this.selectedPresentationId,
          },
        })
        this.slides = res.body
        this.selectedSlideOrder = 0
      } catch (error) {
        this.hasError = true
        Utils.gaTrackEvent('Presentation Editing', 'Copy slide', 'Error when loading slides')
      }

      this.areSlidesLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.presentation-item,
.presentation-item--active {
  padding: 5px 10px;
  cursor: pointer;
  transition: 250ms all ease-in-out;
  display: flex;
  align-items: center;
}

.presentation-item:hover {
  background-color: rgba(231, 232, 235, 0.5);
}

.presentation-item--active {
  background-color: rgb(231, 232, 235);
}

.folder-item {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}
</style>
